import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  Ban, CalendarClock, Loader2, Megaphone, Plus, Printer, Receipt, Repeat, Target, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money, shortDate } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
const BAD = "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
const INFO = "border-primary/40 bg-primary/10 text-primary";

const STAGE_LABEL = {
  identified: "Identified", cultivation: "Cultivation", solicitation: "Solicitation",
  pledged: "Pledged", realised: "Realised", declined: "Declined",
};

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const BLANK_CAMPAIGN = { code: "", name: "", type: "campaign", goal: 0, channel: "digital",
  country: "IN", start_on: "", end_on: "", venue: "", capacity: "", ticket_price: "", description: "" };
const BLANK_PROSPECT = { name: "", type: "major", stage: "identified", ask_amount: 0, owner: "",
  country: "IN", next_action: "", next_action_on: "", notes: "" };
const BLANK_RECURRING = { donor_id: "", fund_id: "", amount: 5000, currency: "INR",
  frequency: "monthly", restriction: "unrestricted", purpose_code: "", payment_method: "bank_transfer",
  mandate_ref: "", next_run_on: "" };

export default function Fundraising() {
  const { can } = useAuth();
  const [state, setState] = useState(null);
  const detail = useRowDetail();
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState("");
  const [campaign, setCampaign] = useState(BLANK_CAMPAIGN);
  const [prospect, setProspect] = useState(BLANK_PROSPECT);
  const [mandate, setMandate] = useState(BLANK_RECURRING);
  const [receiptFor, setReceiptFor] = useState({ donation_id: "", regime: "IN" });
  const [printing, setPrinting] = useState(null);

  const load = useCallback(async () => {
    const [c, p, r, rc, donors, funds, donations] = await Promise.all([
      api.get("/fundraising/campaigns"), api.get("/fundraising/prospects"),
      api.get("/fundraising/recurring"), api.get("/fundraising/receipts"),
      api.get("/charity/donors"), api.get("/charity/funds"), api.get("/charity/donations"),
    ]);
    setState({ campaigns: c.data, prospects: p.data, recurring: r.data, receipts: rc.data,
      donors: donors.data, funds: funds.data, donations: donations.data });
  }, []);

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      if (message) toast.success(typeof message === "function" ? message(res) : message);
      setDialog("");
      await load();
      return res;
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!state) return <Loading label="Loading the fundraising desk" />;
  const { campaigns, prospects, recurring, receipts, donors, funds, donations } = state;
  const receiptable = donations.filter((d) => d.status === "posted");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-022 · Fundraising & donor lifecycle"
        title="Campaigns, pipeline, mandates and receipts in one desk"
        description="Appeals and events with live progress against goal, a major-gift and legacy pipeline with stage history, recurring mandates collected by a nightly scheduled job, and numbered tax receipts for India (80G), the United Kingdom (Gift Aid) and the United States (501(c)(3)) — voided automatically on refund."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-campaign-goal" label="Campaign goal" accent="sky" icon={Target}
             value={money(campaigns.counters.goal, "INR", true)} />
        <Kpi testId="kpi-campaign-raised" label="Raised" accent="emerald" icon={TrendingUp}
             value={money(campaigns.counters.raised, "INR", true)} />
        <Kpi testId="kpi-pipeline" label="Pipeline value" accent="violet" icon={Megaphone}
             value={money(prospects.counters.pipeline_value, "INR", true)} />
        <Kpi testId="kpi-recurring-annualised" label="Recurring / year" accent="teal" icon={Repeat}
             value={money(recurring.counters.annualised, "INR", true)} />
        <Kpi testId="kpi-recurring-due" label="Mandates due" accent="amber" icon={CalendarClock}
             value={recurring.counters.due} />
        <Kpi testId="kpi-receipts" label="Receipts issued" accent="rose" icon={Receipt}
             value={receipts.counters.issued} />
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns & events</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-major">Major & legacy</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring gifts</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">Receipts & refunds</TabsTrigger>
        </TabsList>

        {/* ---------------- campaigns ---------------- */}
        <TabsContent value="campaigns" className="mt-4">
          <Panel testId="panel-campaigns" kicker="FR-DON-003" title="Appeals and fundraising events"
                 actions={can("donor", "write") && (
                   <Dialog open={dialog === "campaign"} onOpenChange={(o) => setDialog(o ? "campaign" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-campaign-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> New campaign
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-lg border-border bg-card">
                       <DialogHeader><DialogTitle>Create a campaign or event</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <F label="Code"><Input value={campaign.code} data-testid="campaign-code-input"
                            onChange={(e) => setCampaign({ ...campaign, code: e.target.value.toUpperCase() })}
                            className="bg-muted/40 font-mono text-[12px]" /></F>
                         <F label="Type">
                           <Select value={campaign.type} onValueChange={(v) => setCampaign({ ...campaign, type: v })}>
                             <SelectTrigger data-testid="campaign-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent><SelectItem value="campaign">Campaign</SelectItem>
                               <SelectItem value="event">Event</SelectItem></SelectContent>
                           </Select>
                         </F>
                         <div className="col-span-2"><F label="Name"><Input value={campaign.name}
                            data-testid="campaign-name-input"
                            onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                            className="bg-muted/40" /></F></div>
                         <F label="Goal"><Input type="number" value={campaign.goal} data-testid="campaign-goal-input"
                            onChange={(e) => setCampaign({ ...campaign, goal: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Channel">
                           <Select value={campaign.channel} onValueChange={(v) => setCampaign({ ...campaign, channel: v })}>
                             <SelectTrigger data-testid="campaign-channel-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>{["digital", "event", "direct_mail", "telephone", "corporate"].map((x) => (
                               <SelectItem key={x} value={x}>{x.replace("_", " ")}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Starts"><Input type="date" value={campaign.start_on} data-testid="campaign-start-input"
                            onChange={(e) => setCampaign({ ...campaign, start_on: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F>
                         <F label="Ends"><Input type="date" value={campaign.end_on} data-testid="campaign-end-input"
                            onChange={(e) => setCampaign({ ...campaign, end_on: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F>
                         <F label="Venue / place"><Input value={campaign.venue} data-testid="campaign-venue-input"
                           onChange={(e) => setCampaign({ ...campaign, venue: e.target.value })}
                           className="bg-muted/40 text-[12px]" /></F>
                        <F label="Capacity"><Input type="number" value={campaign.capacity}
                           data-testid="campaign-capacity-input"
                           onChange={(e) => setCampaign({ ...campaign, capacity: e.target.value })}
                           className="bg-muted/40 font-mono text-[12px]" /></F>
                        <F label="Ticket price"><Input type="number" value={campaign.ticket_price}
                           data-testid="campaign-ticket-input"
                           onChange={(e) => setCampaign({ ...campaign, ticket_price: e.target.value })}
                           className="bg-muted/40 font-mono text-[12px]" /></F>
                        <div className="col-span-2"><F label="Description"><Textarea rows={2}
                            value={campaign.description} data-testid="campaign-description-input"
                            onChange={(e) => setCampaign({ ...campaign, description: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F></div>
                       </div>
                       <Button data-testid="campaign-save-button"
                               disabled={busy === "campaign" || !campaign.code || !campaign.name}
                               onClick={() => act("campaign", () => api.post("/fundraising/campaigns", {
                                 ...campaign, capacity: Number(campaign.capacity) || null,
                                 ticket_price: Number(campaign.ticket_price) || null,
                                 start_on: campaign.start_on || null, end_on: campaign.end_on || null,
                               }), "Campaign created — a matching item is now on the Works Board")
                                .then(() => setCampaign(BLANK_CAMPAIGN))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "campaign" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="campaigns-table"
                   head={<><Th>Campaign</Th><Th>Type</Th><Th>Channel</Th><Th>Window</Th><Th>Goal</Th>
                     <Th>Raised</Th><Th>Progress</Th><Th>Gifts</Th><Th>On the board</Th></>}>
              {campaigns.items.length === 0 && <Empty colSpan={9} />}
              {campaigns.items.map((c) => (
                <tr key={refOf(c)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(c, refOf(c)))} data-testid={`campaign-row-${c.code}`}>
                  <Td><div className="font-medium text-foreground">{c.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{c.code}</div></Td>
                  <Td><Chip className={c.type === "event" ? INFO : OK}>{c.type}</Chip></Td>
                  <Td className="text-[11px] text-muted-foreground">{(c.channel || "").replace("_", " ")}</Td>
                  <Td className="font-mono text-[10.5px]">{shortDate(c.start_on)} → {shortDate(c.end_on)}</Td>
                  <Td className="font-mono text-[11px]">{money(c.goal, "INR", true)}</Td>
                  <Td className="font-mono text-[11px] text-foreground">{money(c.raised, "INR", true)}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all duration-500"
                             style={{ width: `${Math.min(100, c.progress)}%` }} />
                      </div>
                      <span className="font-mono text-[10.5px] text-muted-foreground">{c.progress}%</span>
                    </div>
                  </Td>
                  <Td className="font-mono text-[11px]">{c.gifts}</Td>
                  <Td className="font-mono text-[10.5px]">
                    {c.work_id
                      ? <Link to="/operations/works" onClick={(e) => e.stopPropagation()}
                              data-testid={`campaign-work-link-${c.code}`}
                              className="text-primary hover:underline">{c.work_id}</Link>
                      : <span className="text-muted-foreground">—</span>}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        {/* ---------------- pipeline ---------------- */}
        <TabsContent value="pipeline" className="mt-4 space-y-4">
          <Panel testId="panel-funnel" kicker="FR-DON-005" title="Major-gift and legacy funnel">
            <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3 lg:grid-cols-6">
              {prospects.funnel.map((s) => (
                <div key={s.stage} data-testid={`funnel-${s.stage}`}
                     className="rounded-xl border border-border bg-muted/25 p-3 transition-colors duration-200 hover:border-primary/40">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{STAGE_LABEL[s.stage]}</div>
                  <div className="mt-1 text-[19px] font-bold leading-none text-foreground">{s.count}</div>
                  <div className="mt-1 font-mono text-[10.5px] text-muted-foreground">{money(s.value, "INR", true)}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel testId="panel-prospects" kicker="Pipeline" title="Prospects and legacy pledges"
                 actions={can("donor", "write") && (
                   <Dialog open={dialog === "prospect"} onOpenChange={(o) => setDialog(o ? "prospect" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-prospect-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> Add prospect
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-lg border-border bg-card">
                       <DialogHeader><DialogTitle>Add a prospect</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <div className="col-span-2"><F label="Name"><Input value={prospect.name}
                            data-testid="prospect-name-input"
                            onChange={(e) => setProspect({ ...prospect, name: e.target.value })}
                            className="bg-muted/40" /></F></div>
                         <F label="Type">
                           <Select value={prospect.type} onValueChange={(v) => setProspect({ ...prospect, type: v })}>
                             <SelectTrigger data-testid="prospect-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent><SelectItem value="major">Major gift</SelectItem>
                               <SelectItem value="legacy">Legacy / bequest</SelectItem>
                               <SelectItem value="corporate">Corporate</SelectItem></SelectContent>
                           </Select>
                         </F>
                         <F label="Ask amount"><Input type="number" value={prospect.ask_amount}
                            data-testid="prospect-ask-input"
                            onChange={(e) => setProspect({ ...prospect, ask_amount: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Owner"><Input value={prospect.owner} data-testid="prospect-owner-input"
                            onChange={(e) => setProspect({ ...prospect, owner: e.target.value })}
                            className="bg-muted/40 font-mono text-[12px]" /></F>
                         <F label="Next action on"><Input type="date" value={prospect.next_action_on}
                            data-testid="prospect-date-input"
                            onChange={(e) => setProspect({ ...prospect, next_action_on: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F>
                         <div className="col-span-2"><F label="Next action"><Input value={prospect.next_action}
                            data-testid="prospect-action-input"
                            onChange={(e) => setProspect({ ...prospect, next_action: e.target.value })}
                            className="bg-muted/40" /></F></div>
                       </div>
                       <Button data-testid="prospect-save-button" disabled={busy === "prospect" || !prospect.name}
                               onClick={() => act("prospect", () => api.post("/fundraising/prospects", {
                                 ...prospect, next_action_on: prospect.next_action_on || null,
                               }), "Prospect added").then(() => setProspect(BLANK_PROSPECT))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "prospect" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="prospects-table"
                   head={<><Th>Prospect</Th><Th>Type</Th><Th>Ask</Th><Th>Stage</Th><Th>Owner</Th>
                     <Th>Next action</Th><Th>Move to</Th></>}>
              {prospects.items.length === 0 && <Empty colSpan={7} />}
              {prospects.items.map((p) => (
                <tr key={refOf(p)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(p, refOf(p)))} data-testid={`prospect-row-${p.id}`}>
                  <Td><div className="font-medium text-foreground">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">{p.notes}</div></Td>
                  <Td><Chip className={p.type === "legacy" ? INFO : OK}>{p.type}</Chip></Td>
                  <Td className="font-mono text-[11px]">{money(p.ask_amount, "INR", true)}</Td>
                  <Td><Chip testId={`prospect-stage-${p.id}`} className={WARN}>{STAGE_LABEL[p.stage] || p.stage}</Chip></Td>
                  <Td className="font-mono text-[10.5px] text-muted-foreground">{p.owner}</Td>
                  <Td className="text-[11px]">{p.next_action}
                    <div className="font-mono text-[10px] text-muted-foreground">{p.next_action_on}</div></Td>
                  <Td>
                    {can("donor", "write") && (
                      <Select value="" onValueChange={(v) => act(`stage-${p.id}`,
                        () => api.post(`/fundraising/prospects/${p.id}/stage`, { stage: v, note: "Moved from the pipeline board" }),
                        `${p.name} moved to ${STAGE_LABEL[v]}`)}>
                        <SelectTrigger data-testid={`prospect-move-${p.id}`} className="h-7 w-[130px] bg-muted/40 text-[11px]">
                          <SelectValue placeholder="Change stage" />
                        </SelectTrigger>
                        <SelectContent>
                          {prospects.stages.filter((s) => s !== p.stage).map((s) => (
                            <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        {/* ---------------- recurring ---------------- */}
        <TabsContent value="recurring" className="mt-4">
          <Panel testId="panel-recurring" kicker="FR-DON-006" title="Recurring gift mandates"
                 actions={(
                   <div className="flex gap-2">
                     {can("donation", "write") && (
                       <Button size="sm" variant="outline" data-testid="collect-due-button"
                               disabled={busy === "collect"}
                               onClick={() => act("collect", () => api.post("/fundraising/recurring/collect"),
                                 (r) => `${r.data.collected.length} collected, ${r.data.blocked.length} blocked`)}
                               className="h-8 rounded-full border-border text-[11px]">
                         {busy === "collect" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                           : <Repeat className="mr-1 h-3.5 w-3.5" />} Collect due now
                       </Button>
                     )}
                     {can("donor", "write") && (
                       <Dialog open={dialog === "mandate"} onOpenChange={(o) => setDialog(o ? "mandate" : "")}>
                         <DialogTrigger asChild>
                           <Button size="sm" data-testid="new-mandate-button"
                                   className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                             <Plus className="mr-1 h-3.5 w-3.5" /> New mandate
                           </Button>
                         </DialogTrigger>
                         <DialogContent className="max-w-lg border-border bg-card">
                           <DialogHeader><DialogTitle>Set up a recurring gift</DialogTitle></DialogHeader>
                           <div className="grid grid-cols-2 gap-3">
                             <div className="col-span-2"><F label="Donor">
                               <Select value={mandate.donor_id} onValueChange={(v) => setMandate({ ...mandate, donor_id: v })}>
                                 <SelectTrigger data-testid="mandate-donor-select" className="bg-muted/40">
                                   <SelectValue placeholder="Choose a donor" /></SelectTrigger>
                                 <SelectContent className="max-h-64">{donors.map((d) => (
                                   <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
                               </Select></F></div>
                             <F label="Fund">
                               <Select value={mandate.fund_id} onValueChange={(v) => setMandate({ ...mandate, fund_id: v })}>
                                 <SelectTrigger data-testid="mandate-fund-select" className="bg-muted/40">
                                   <SelectValue placeholder="Fund" /></SelectTrigger>
                                 <SelectContent>{funds.map((f) => (
                                   <SelectItem key={f.id} value={f.id}>{f.code}</SelectItem>))}</SelectContent>
                               </Select>
                             </F>
                             <F label="Amount"><Input type="number" value={mandate.amount}
                                data-testid="mandate-amount-input"
                                onChange={(e) => setMandate({ ...mandate, amount: Number(e.target.value) })}
                                className="bg-muted/40 font-mono" /></F>
                             <F label="Frequency">
                               <Select value={mandate.frequency} onValueChange={(v) => setMandate({ ...mandate, frequency: v })}>
                                 <SelectTrigger data-testid="mandate-frequency-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                                 <SelectContent><SelectItem value="monthly">Monthly</SelectItem>
                                   <SelectItem value="quarterly">Quarterly</SelectItem>
                                   <SelectItem value="annual">Annual</SelectItem></SelectContent>
                               </Select>
                             </F>
                             <F label="First collection"><Input type="date" value={mandate.next_run_on}
                                data-testid="mandate-date-input"
                                onChange={(e) => setMandate({ ...mandate, next_run_on: e.target.value })}
                                className="bg-muted/40 text-[12px]" /></F>
                           </div>
                           <Button data-testid="mandate-save-button"
                                   disabled={busy === "mandate" || !mandate.donor_id || !mandate.fund_id}
                                   onClick={() => act("mandate", () => api.post("/fundraising/recurring", {
                                     ...mandate, next_run_on: mandate.next_run_on || null,
                                   }), "Mandate created").then(() => setMandate(BLANK_RECURRING))}
                                   className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                             {busy === "mandate" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create mandate
                           </Button>
                         </DialogContent>
                       </Dialog>
                     )}
                   </div>
                 )}>
            <Table testId="recurring-table"
                   head={<><Th>Donor</Th><Th>Fund</Th><Th>Amount</Th><Th>Frequency</Th><Th>Next run</Th>
                     <Th>Collected</Th><Th>Status</Th><Th /></>}>
              {recurring.items.length === 0 && <Empty colSpan={8} />}
              {recurring.items.map((r) => (
                <tr key={refOf(r)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(r, refOf(r)))} data-testid={`recurring-row-${r.id}`}>
                  <Td><div className="font-medium text-foreground">{r.donor_name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.mandate_ref}</div></Td>
                  <Td className="font-mono text-[11px]">{r.fund_code}</Td>
                  <Td className="font-mono text-[11px]">{money(r.amount, r.currency)}</Td>
                  <Td className="text-[11px]">{r.frequency}</Td>
                  <Td className="font-mono text-[11px]">{r.next_run_on || "—"}</Td>
                  <Td className="font-mono text-[11px]">{r.collected_count} · {money(r.collected_value, r.currency, true)}</Td>
                  <Td><Chip testId={`recurring-status-${r.id}`}
                            className={r.status === "active" ? OK : r.status === "paused" ? WARN : BAD}>
                    {r.status}</Chip>
                    {r.paused_reason && <div className="mt-1 max-w-[180px] text-[10px] text-muted-foreground">{r.paused_reason}</div>}
                  </Td>
                  <Td>
                    {can("donor", "write") && r.status !== "cancelled" && (
                      <Button size="sm" variant="outline" data-testid={`recurring-toggle-${r.id}`}
                              disabled={busy === `st-${r.id}`}
                              onClick={() => act(`st-${r.id}`, () => api.post(`/fundraising/recurring/${r.id}/status`,
                                { status: r.status === "active" ? "paused" : "active" }),
                                r.status === "active" ? "Mandate paused" : "Mandate resumed")}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">
                        {r.status === "active" ? "Pause" : "Resume"}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
            <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              A platform-scheduled job collects every due mandate at 02:30 daily. A mandate that is
              blocked by the decision engine is paused with the reason recorded.
            </div>
          </Panel>
        </TabsContent>

        {/* ---------------- receipts ---------------- */}
        <TabsContent value="receipts" className="mt-4 space-y-4">
          <Panel testId="panel-issue-receipt" kicker="FR-DON-007" title="Issue a tax receipt">
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-[1fr_200px_auto]">
              <F label="Posted donation">
                <Select value={receiptFor.donation_id}
                        onValueChange={(v) => setReceiptFor({ ...receiptFor, donation_id: v })}>
                  <SelectTrigger data-testid="receipt-donation-select" className="bg-muted/40">
                    <SelectValue placeholder="Choose a donation" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {receiptable.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.reference} · {d.donor_name} · {money(d.base_amount, "INR", true)}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Regime">
                <Select value={receiptFor.regime} onValueChange={(v) => setReceiptFor({ ...receiptFor, regime: v })}>
                  <SelectTrigger data-testid="receipt-regime-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(receipts.regimes).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>))}</SelectContent>
                </Select>
              </F>
              <div className="flex items-end">
                <Button data-testid="issue-receipt-button"
                        disabled={busy === "receipt" || !receiptFor.donation_id || !can("donation", "write")}
                        onClick={() => act("receipt",
                          () => api.post(`/fundraising/donations/${receiptFor.donation_id}/receipt`,
                            { regime: receiptFor.regime }),
                          (r) => `Receipt ${r.data.receipt_no} issued`)}
                        className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "receipt" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Issue receipt
                </Button>
              </div>
            </div>
          </Panel>

          <Panel testId="panel-receipts" kicker="Register"
                 title={`${receipts.counters.issued} issued · ${receipts.counters.void} void · ${money(receipts.counters.value, "INR", true)}`}>
            <Table testId="receipts-table"
                   head={<><Th>Receipt no.</Th><Th>Regime</Th><Th>Donor</Th><Th>Amount</Th><Th>Gift date</Th>
                     <Th>Fund</Th><Th>Status</Th><Th /></>}>
              {receipts.items.length === 0 && <Empty colSpan={8} label="No receipts issued yet" />}
              {receipts.items.map((r) => (
                <tr key={refOf(r)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(r, refOf(r)))} data-testid={`receipt-row-${r.receipt_no}`}>
                  <Td className="font-mono text-[11px] text-foreground">{r.receipt_no}</Td>
                  <Td><Chip className={INFO}>{r.regime}</Chip></Td>
                  <Td className="font-medium text-foreground">{r.donor_name}</Td>
                  <Td className="font-mono text-[11px]">{money(r.amount, r.currency)}</Td>
                  <Td className="font-mono text-[11px]">{r.donation_date}</Td>
                  <Td className="font-mono text-[11px]">{r.fund_code}</Td>
                  <Td><Chip testId={`receipt-status-${r.receipt_no}`} className={r.status === "issued" ? OK : BAD}>
                    {r.status}</Chip></Td>
                  <Td>
                    <Button size="sm" variant="outline" data-testid={`print-receipt-${r.receipt_no}`}
                            onClick={() => setPrinting(r)}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">
                      <Printer className="mr-1 h-3 w-3" /> View
                    </Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>

          <RefundPanel donations={receiptable} can={can} act={act} busy={busy} />
        </TabsContent>
      </Tabs>

      <ReceiptPrint receipt={printing} onClose={() => setPrinting(null)} />

      {detail.sheet}
    </div>
  );
}

const RefundPanel = ({ donations, can, act, busy }) => {
  const [form, setForm] = useState({ donation_id: "", amount: "", reason: "" });
  if (!can("finance", "approve")) return null;
  return (
    <Panel testId="panel-refund" kicker="FR-DON-008" title="Refund a donation">
      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-[1fr_140px_1fr_auto]">
        <F label="Donation">
          <Select value={form.donation_id} onValueChange={(v) => setForm({ ...form, donation_id: v })}>
            <SelectTrigger data-testid="refund-donation-select" className="bg-muted/40">
              <SelectValue placeholder="Choose a donation" /></SelectTrigger>
            <SelectContent className="max-h-72">{donations.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.reference} · {d.donor_name}</SelectItem>))}</SelectContent>
          </Select>
        </F>
        <F label="Amount"><Input type="number" value={form.amount} data-testid="refund-amount-input"
           onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-muted/40 font-mono" /></F>
        <F label="Reason (min 10 characters)"><Input value={form.reason} data-testid="refund-reason-input"
           onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-muted/40" /></F>
        <div className="flex items-end">
          <Button variant="outline" data-testid="refund-button"
                  disabled={busy === "refund" || !form.donation_id || !form.amount || form.reason.trim().length < 10}
                  onClick={() => act("refund", () => api.post(`/fundraising/donations/${form.donation_id}/refund`,
                    { amount: Number(form.amount), reason: form.reason }),
                    (r) => `Refunded · ${r.data.receipts_voided} receipt(s) voided`)
                    .then(() => setForm({ donation_id: "", amount: "", reason: "" }))}
                  className="w-full rounded-full border-destructive/40 text-[12px] text-destructive hover:bg-destructive/10">
            {busy === "refund" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <Ban className="mr-1.5 h-3.5 w-3.5" />} Refund
          </Button>
        </div>
      </div>
      <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
        A refund posts a reversing ledger entry, reduces the fund balance and voids any receipt already issued.
      </div>
    </Panel>
  );
};

const ReceiptPrint = ({ receipt, onClose }) => (
  <Dialog open={Boolean(receipt)} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl border-border bg-card" data-testid="receipt-print-dialog">
      <DialogHeader><DialogTitle>Receipt {receipt?.receipt_no}</DialogTitle></DialogHeader>
      {receipt && (
        <>
          <div id="receipt-print-area" className="max-h-[65vh] overflow-y-auto rounded-xl border border-border bg-white p-8 text-[#1c1f2e]">
            <div className="flex items-start justify-between border-b border-[#e3e5ee] pb-4">
              <div>
                <div className="text-[18px] font-bold">{receipt.organisation}</div>
                <div className="mt-1 text-[11px] text-[#6b6f80]">
                  {(receipt.registration_refs || []).join(" · ")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[1.5px] text-[#6b6f80]">Official receipt</div>
                <div className="font-mono text-[13px] font-bold">{receipt.receipt_no}</div>
                <div className="text-[11px] text-[#6b6f80]">{receipt.regime_label}</div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-[12px]">
              {[["Received from", receipt.donor_name], ["Gift date", receipt.donation_date],
                ["Amount", money(receipt.amount, receipt.currency)], ["Fund", receipt.fund_code],
                ["Restriction", receipt.restriction], ["Donation reference", receipt.donation_reference],
                ["Issued by", receipt.issued_by], ["Issued at", dateTime(receipt.issued_at)]].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-wide text-[#8a8fa3]">{k}</div>
                  <div className="font-medium">{String(v ?? "—")}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-lg bg-[#f4f5f9] p-3.5 text-[11.5px] leading-relaxed">
              {receipt.statement}
            </div>
            <div className="mt-4 text-[10.5px] text-[#8a8fa3]">
              Recognised by {receipt.authority}. This receipt is void if the donation is refunded.
              {receipt.status === "void" && (
                <span className="ml-1 font-bold uppercase text-red-600">This receipt has been voided.</span>
              )}
            </div>
          </div>
          <Button data-testid="receipt-print-button" onClick={() => window.print()}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Printer className="mr-2 h-4 w-4" /> Print or save as PDF
          </Button>
        </>
      )}
    </DialogContent>
  </Dialog>
);
